import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Storage } from '@ionic/storage-angular';
import { Device } from '@capacitor/device';
import { BehaviorSubject } from 'rxjs';

const LANGUAGE_KEY = 'app_language';
const SUPPORTED_LANGUAGES = ['en', 'es', 'fr'];
const DEFAULT_LANGUAGE = 'en';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private currentLang$ = new BehaviorSubject<string>(DEFAULT_LANGUAGE);

  get currentLanguage$() {
    return this.currentLang$.asObservable();
  }

  get currentLanguage(): string {
    return this.currentLang$.value;
  }

  get supportedLanguages() {
    return SUPPORTED_LANGUAGES;
  }

  constructor(
    private translate: TranslateService,
    private storage: Storage
  ) {}

  async initializeLanguage(): Promise<void> {
    await this.storage.create();

    this.translate.addLangs(SUPPORTED_LANGUAGES);
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);

    // Use stored preference if available, otherwise default to English
    const storedLang = await this.storage.get(LANGUAGE_KEY);
    if (storedLang && SUPPORTED_LANGUAGES.includes(storedLang)) {
      this.setLanguage(storedLang);
      return;
    }

    this.setLanguage(DEFAULT_LANGUAGE);
  }

  async setLanguage(lang: string): Promise<void> {
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
      lang = DEFAULT_LANGUAGE;
    }
    this.translate.use(lang);
    this.currentLang$.next(lang);
    await this.storage.set(LANGUAGE_KEY, lang);
  }

  private async detectDeviceLanguage(): Promise<string> {
    try {
      const result = await Device.getLanguageCode();
      const langCode = result.value?.substring(0, 2)?.toLowerCase();
      if (langCode && SUPPORTED_LANGUAGES.includes(langCode)) {
        return langCode;
      }
    } catch {
      // Fallback to navigator
      const navLang = navigator.language?.substring(0, 2)?.toLowerCase();
      if (navLang && SUPPORTED_LANGUAGES.includes(navLang)) {
        return navLang;
      }
    }
    return DEFAULT_LANGUAGE;
  }

  getLanguageLabel(lang: string): string {
    const labels: Record<string, string> = {
      en: 'English',
      es: 'Español',
      fr: 'Français'
    };
    return labels[lang] || lang;
  }
}
